import { useState, useEffect, useMemo } from 'react';
import {
  Skeleton,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
} from '@nextui-org/react';
import { Link } from 'wouter';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { supabase } from 'common/src/supabase';
import {
  Settlement,
  ShipmentBox,
  ReceiverMapping,
  ExporterReceiver,
} from 'common/src/utils/types';
import { getFruticReceiverMapping } from 'common/src/api/frutic';
import {
  DashboardSettlementShipmentRow,
  getDashboardSettlementShipments,
} from 'common/src/api/shipments';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

type SettlementShipment = ShipmentBox | DashboardSettlementShipmentRow;

const SettlementsWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile, session } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [shipmentBoxes, setShipmentBoxes] = useState<SettlementShipment[]>([]);
  const [receiverMappings, setReceiverMappings] = useState<ReceiverMapping[]>(
    []
  );
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);

  // Fetch data
  useEffect(() => {
    if (
      !selectedSpecies?.id ||
      !selectedSeason ||
      !userProfile?.exporter_id ||
      !session
    ) {
      return;
    }

    let active = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch settlements (always from Supabase)
        const settlementsRes = await supabase
          .from('settlements')
          .select('id, reference_number, receiver, upload_status, ship_trip_id')
          .eq('exporter_id', userProfile.exporter_id)
          .eq('season', selectedSeason)
          .eq('upload_status', 'approved');

        if (settlementsRes.error) throw settlementsRes.error;

        const shipmentBoxesData = await getDashboardSettlementShipments(
          userProfile.exporter_id!,
          selectedSeason,
          selectedSpecies.id
        );
        if (!shipmentBoxesData) throw new Error('Error fetching shipment data');

        // Fetch receiver definitions and mappings used to standardize raw receiver names.
        const [receiversRes, mappings] = await Promise.all([
          supabase
            .from('exporter_receivers')
            .select('*')
            .eq('exporter_id', userProfile.exporter_id),
          getFruticReceiverMapping(userProfile),
        ]);

        if (!active) return;
        if (receiversRes.error) throw receiversRes.error;

        setSettlements((settlementsRes.data as Settlement[]) || []);
        setShipmentBoxes(shipmentBoxesData);
        setExporterReceivers((receiversRes.data as ExporterReceiver[]) || []);
        setReceiverMappings(
          ((mappings as unknown as ReceiverMapping[]) ||
            []) as ReceiverMapping[]
        );
      } catch (error) {
        if (!active) return;
        console.error('Error fetching settlements data:', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [selectedSpecies?.id, selectedSeason, userProfile?.exporter_id, session]);

  // Calculate client summary
  const clientSummary = useMemo(() => {
    // Build lookup from raw receiver name (normalized) to mapped exporter receiver display name
    const nameMapping = new Map<string, string>();
    receiverMappings.forEach((m) => {
      const key = (m.frutic_receiver_name || '').trim().toUpperCase();
      const mappedName = m.exporter_receivers?.name || '';
      if (key && mappedName) nameMapping.set(key, mappedName);
    });

    const receiverIdToName = new Map<number, string>();
    exporterReceivers.forEach((receiver) => {
      const name = (receiver.name || '').trim();
      if (!name) return;
      receiverIdToName.set(receiver.id, name);
    });

    const resolveShipmentReceiver = (box: SettlementShipment) => {
      const raw = (box.receiver || '').toString().trim();
      if (!raw) return null;

      const rawKey = raw.toUpperCase();
      const explicitReceiverId = Number(box.exporter_receiver_id);
      const explicitName =
        Number.isFinite(explicitReceiverId) && explicitReceiverId > 0
          ? receiverIdToName.get(explicitReceiverId)
          : null;
      const mappedName = nameMapping.get(rawKey);
      const display = explicitName || mappedName || raw;

      return {
        display,
        key: display.toUpperCase(),
        isMapped: Boolean(explicitName || mappedName),
      };
    };

    // Collect unique clients from shipment boxes using mapped names when available
    const clientKeyToDisplay = new Map<string, string>();
    const clientKeyIsMapped = new Map<string, boolean>();
    shipmentBoxes.forEach((sb) => {
      const resolved = resolveShipmentReceiver(sb);
      if (!resolved) return;
      // store display name for this key
      clientKeyToDisplay.set(resolved.key, resolved.display);
      // record if mapped
      if (!clientKeyIsMapped.has(resolved.key)) {
        clientKeyIsMapped.set(resolved.key, resolved.isMapped);
      } else {
        // If any raw receiver contributing to this display group is unresolved,
        // keep the group marked as unmapped so the missing FK is visible.
        clientKeyIsMapped.set(
          resolved.key,
          Boolean(clientKeyIsMapped.get(resolved.key) && resolved.isMapped)
        );
      }
    });

    const clients = Array.from(clientKeyToDisplay.keys());

    // Create a Set of settled reference_numbers for fast lookup
    const settledReferences = new Set(
      settlements.map((s) => s.reference_number)
    );

    // Calculate metrics for each client
    return clients.map((clienteKey) => {
      const displayName = clientKeyToDisplay.get(clienteKey) || clienteKey;
      const mappedFlag = clientKeyIsMapped.get(clienteKey) || false;

      // Get all unique shipments (reference_numbers) for this client (mapped)
      const clientShipments = new Map<string, { eta: string | null }>();

      shipmentBoxes.forEach((sb) => {
        const resolved = resolveShipmentReceiver(sb);
        if (!resolved) return;
        if (resolved.key === clienteKey) {
          if (!clientShipments.has(sb.reference_number)) {
            clientShipments.set(sb.reference_number, { eta: sb.ship_eta });
          }
        }
      });

      const despachos = clientShipments.size;

      // Count liquidated shipments
      let liquidados = 0;
      let porLiquidar = 0;
      let atrasados = 0;

      const today = new Date();
      const thirtyDaysAgo = new Date(
        today.getTime() - 30 * 24 * 60 * 60 * 1000
      );

      clientShipments.forEach((shipmentData, refNumber) => {
        const isLiquidated = settledReferences.has(refNumber);

        if (isLiquidated) {
          liquidados++;
        } else {
          porLiquidar++;

          // Check if it's overdue (ETA > 30 days and not liquidated)
          if (shipmentData.eta) {
            const eta = new Date(shipmentData.eta);
            if (eta < thirtyDaysAgo) {
              atrasados++;
            }
          }
        }
      });

      return {
        cliente: displayName,
        despachos,
        liquidados,
        porLiquidar,
        atrasados,
        isMapped: mappedFlag,
      };
    });
  }, [settlements, shipmentBoxes, receiverMappings, exporterReceivers]);

  // Calculate totals
  const totals = useMemo(() => {
    return {
      despachos: clientSummary.reduce((sum, row) => sum + row.despachos, 0),
      liquidados: clientSummary.reduce((sum, row) => sum + row.liquidados, 0),
      porLiquidar: clientSummary.reduce((sum, row) => sum + row.porLiquidar, 0),
      atrasados: clientSummary.reduce((sum, row) => sum + row.atrasados, 0),
    };
  }, [clientSummary]);

  if (loading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-12 w-2/3 rounded-lg' />
        <Skeleton className='h-48 w-full rounded-lg' />
      </div>
    );
  }

  // Preparar datos para mostrar - si no hay datos, mostrar tabla con ceros
  const dataToShow = clientSummary.length > 0 ? clientSummary : [];
  const totalsToShow =
    clientSummary.length > 0
      ? totals
      : {
          despachos: 0,
          liquidados: 0,
          porLiquidar: 0,
          atrasados: 0,
        };

  return (
    <div className='h-full flex flex-col'>
      {/* Header - Fixed */}
      <div className='flex-shrink-0 mb-4'>
        <h3 className='text-lg font-bold'>{t('Liquidaciones')}</h3>
        {settlements.length === 0 && shipmentBoxes.length === 0 && (
          <p className='text-xs text-default-300 mt-1'>
            {t('No hay datos disponibles para esta temporada')}
          </p>
        )}
      </div>

      {/* Table - Scrollable */}
      <div className='flex-1 overflow-y-auto min-h-0'>
        <Table
          removeWrapper
          aria-label='Tabla de entrega de liquidaciones por cliente'
          classNames={{
            th: 'text-xs sticky top-0 z-10 bg-default-100 shadow-sm',
            td: 'text-sm',
          }}
        >
          <TableHeader>
            <TableColumn>{t('CLIENTE')}</TableColumn>
            <TableColumn className='text-right'>{t('DESPACHOS')}</TableColumn>
            <TableColumn className='text-right'>{t('LIQUIDADOS')}</TableColumn>
            <TableColumn className='text-right'>
              {t('POR LIQUIDAR')}
            </TableColumn>
            <TableColumn className='text-right'>{t('ATRASADOS')}</TableColumn>
          </TableHeader>
          <TableBody>
            {[
              ...dataToShow,
              {
                cliente: t('TOTAL'),
                despachos: totalsToShow.despachos,
                liquidados: totalsToShow.liquidados,
                porLiquidar: totalsToShow.porLiquidar,
                atrasados: totalsToShow.atrasados,
              },
            ].map((row, idx) => {
              const isTotal = row.cliente === t('TOTAL');
              const hasAtrasados = row.atrasados > 0;

              return (
                <TableRow key={idx}>
                  <TableCell className={isTotal ? 'font-bold' : ''}>
                    <div className='flex items-center gap-2'>
                      <span>{row.cliente}</span>
                      {!isTotal && row.isMapped === false && (
                        <Tooltip
                          content={t(
                            'Mapéalo en Configuración para estandarizar tu base de clientes.'
                          )}
                        >
                          <Link href='/settings'>
                            <a>
                              <Chip size='sm' color='warning' variant='flat'>
                                {t('sin mapear')}
                              </Chip>
                            </a>
                          </Link>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {row.despachos}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {row.liquidados}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {row.porLiquidar}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''} ${hasAtrasados ? 'text-red-600' : ''}`}
                  >
                    {row.atrasados}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SettlementsWidget;
